import React, { useEffect, useState } from 'react';
import { modeAPI } from '../services/api';
import './CompanyUpdateModal.css';

const COMPANY_SIZE_OPTIONS = ['1-10', '11-50', '51-200', '201-500', '501-1000', '1000+'];

const CompanyUpdateModal = ({ isOpen, onClose, companyId, onSuccess }) => {
  const [form, setForm] = useState({
    name: '',
    description: '',
    website: '',
    industry: '',
    company_size: '',
    founded_year: '',
    location: '',
    address: '',
    phone: '',
    contact_email: '',
    government_business_id: '',
  });
  const [loading, setLoading] = useState(false);
  const [fetching, setFetching] = useState(false);
  const [error, setError] = useState(null);
  const [submitError, setSubmitError] = useState(null);

  useEffect(() => {
    if (!isOpen || !companyId) return;
    setFetching(true);
    setError(null);
    modeAPI.getCompanyProfile(companyId)
      .then((res) => {
        if (res.success && res.data) {
          const d = res.data;
          setForm({
            name: d.name ?? '',
            description: d.description ?? '',
            website: d.website ?? '',
            industry: d.industry ?? '',
            company_size: d.company_size ?? '',
            founded_year: d.founded_year ? String(d.founded_year) : '',
            location: d.location ?? '',
            address: d.address ?? '',
            phone: d.phone ?? '',
            contact_email: d.contact_email ?? '',
            government_business_id: d.government_business_id ?? '',
          });
        } else setError('Could not load company.');
      })
      .catch(() => setError('Could not load company.'))
      .finally(() => setFetching(false));
  }, [isOpen, companyId]);

  if (!isOpen) return null;

  const handleBackdropClick = (e) => {
    if (e.target === e.currentTarget) onClose();
  };

  const handleChange = (field, value) => {
    setForm((prev) => ({ ...prev, [field]: value }));
    setSubmitError(null);
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    setSubmitError(null);
    setLoading(true);
    const payload = {
      name: form.name || undefined,
      description: form.description || undefined,
      website: form.website || undefined,
      industry: form.industry || undefined,
      company_size: form.company_size || undefined,
      founded_year: form.founded_year ? parseInt(form.founded_year, 10) : undefined,
      location: form.location || undefined,
      address: form.address || undefined,
      phone: form.phone || undefined,
      email: form.contact_email || undefined,
      government_business_id: form.government_business_id || undefined,
    };
    modeAPI.updateCompanyProfile(companyId, payload)
      .then((res) => {
        if (res.success) {
          onSuccess && onSuccess(res.data);
          onClose();
        } else setSubmitError(res.message || 'Update failed.');
      })
      .catch((err) => {
        const msg = err?.data?.message || err?.message || 'Update failed.';
        setSubmitError(msg);
      })
      .finally(() => setLoading(false));
  };

  return (
    <div className="company-update-modal-overlay" onClick={handleBackdropClick}>
      <div className="company-update-modal-content">
        <div className="company-update-modal-header">
          <h3>Update company</h3>
          <button type="button" className="company-update-modal-close" onClick={onClose} aria-label="Close">&times;</button>
        </div>
        {fetching && (
          <div className="company-update-loading"><span className="spinner"></span> Loading…</div>
        )}
        {error && <div className="company-update-error">{error}</div>}
        {!fetching && !error && (
          <form onSubmit={handleSubmit} className="company-update-form">
            {submitError && <div className="company-update-submit-error">{submitError}</div>}
            <label className="company-update-field">
              <span>Company name</span>
              <input
                type="text"
                value={form.name}
                onChange={(e) => handleChange('name', e.target.value)}
                placeholder="Company name"
                maxLength={255}
              />
            </label>
            <label className="company-update-field">
              <span>Description</span>
              <textarea
                value={form.description}
                onChange={(e) => handleChange('description', e.target.value)}
                placeholder="Brief description"
                rows={3}
                maxLength={5000}
              />
            </label>
            <label className="company-update-field">
              <span>Website</span>
              <input
                type="url"
                value={form.website}
                onChange={(e) => handleChange('website', e.target.value)}
                placeholder="https://"
                maxLength={500}
              />
            </label>
            <label className="company-update-field">
              <span>Industry</span>
              <input
                type="text"
                value={form.industry}
                onChange={(e) => handleChange('industry', e.target.value)}
                placeholder="e.g. Technology"
                maxLength={255}
              />
            </label>
            <label className="company-update-field">
              <span>Company size</span>
              <select
                value={form.company_size}
                onChange={(e) => handleChange('company_size', e.target.value)}
              >
                <option value="">Select</option>
                {COMPANY_SIZE_OPTIONS.map((opt) => (
                  <option key={opt} value={opt}>{opt}</option>
                ))}
              </select>
            </label>
            <label className="company-update-field">
              <span>Founded year</span>
              <input
                type="number"
                value={form.founded_year}
                onChange={(e) => handleChange('founded_year', e.target.value)}
                placeholder="e.g. 2020"
                min={1800}
                max={new Date().getFullYear()}
              />
            </label>
            <label className="company-update-field">
              <span>Location</span>
              <input
                type="text"
                value={form.location}
                onChange={(e) => handleChange('location', e.target.value)}
                placeholder="City, Country"
                maxLength={255}
              />
            </label>
            <label className="company-update-field">
              <span>Address</span>
              <input
                type="text"
                value={form.address}
                onChange={(e) => handleChange('address', e.target.value)}
                placeholder="Street address"
                maxLength={500}
              />
            </label>
            <label className="company-update-field">
              <span>Phone</span>
              <input
                type="text"
                value={form.phone}
                onChange={(e) => handleChange('phone', e.target.value)}
                placeholder="Contact phone"
                maxLength={50}
              />
            </label>
            <label className="company-update-field">
              <span>Contact email</span>
              <input
                type="email"
                value={form.contact_email}
                onChange={(e) => handleChange('contact_email', e.target.value)}
                placeholder="Public contact email"
                maxLength={255}
              />
            </label>
            <label className="company-update-field">
              <span>Government / Business ID</span>
              <input
                type="text"
                value={form.government_business_id}
                onChange={(e) => handleChange('government_business_id', e.target.value)}
                placeholder="Optional"
                maxLength={100}
              />
            </label>
            <div className="company-update-actions">
              <button type="button" className="company-update-btn cancel" onClick={onClose} disabled={loading}>
                Cancel
              </button>
              <button type="submit" className="company-update-btn submit" disabled={loading}>
                {loading ? <span className="spinner small"></span> : 'Save changes'}
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
};

export default CompanyUpdateModal;
